import { OrderLoadError, OrderLoading, OrderNotFound } from '@/components/OrderLoadStates'
import { useOrderResourceScope } from '@/features/auth/hooks/useOrderResourceScope'
import React from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'

interface OrderScopeGuardProps {
  children: React.ReactNode
  /** When set, overrides the `orderId` route param (e.g. order id from navigation state). */
  orderId?: string
}

/**
 * Blocks order deep-link routes until GET /auth/resource-scope/order/{id} returns 204.
 * Skips the probe when no order id is present (e.g. patient-only in-progress URLs).
 *
 * Not used on `/order/in-progress`: that page's own order fetch is org-scoped
 * server-side and renders the same shared not-found / error states, so a probe
 * there would only add a second serial request and a second loader.
 */
export const OrderScopeGuard: React.FC<OrderScopeGuardProps> = ({ children, orderId: orderIdProp }) => {
  const location = useLocation()
  const params = useParams<{ orderId?: string }>()
  const orderId = (orderIdProp ?? params.orderId)?.trim()
  const scope = useOrderResourceScope(orderId)

  if (!orderId) {
    return <>{children}</>
  }

  switch (scope.status) {
    case 'idle':
    case 'loading':
      return <OrderLoading />
    case 'in_scope':
      return <>{children}</>
    case 'not_found':
      return <OrderNotFound />
    case 'unauthorized':
      return <Navigate to="/login" state={{ from: location }} replace />
    case 'error':
      return <OrderLoadError title="Unable to verify order" message={scope.message} />
    default: {
      const _exhaustiveStatus: never = scope
      void _exhaustiveStatus
      return <OrderLoading />
    }
  }
}

export default OrderScopeGuard
